// Blog post data — kept as a single source of truth.
// Each post:
//   slug         → /blog/<slug>
//   title        → page <h1> and <title>
//   description  → meta description and listing summary
//   keywords     → meta keywords
//   author       → display name
//   datePublished → ISO date string
//   readingTime  → "8 min read"
//   coverImage   → URL
//   coverAlt     → descriptive alt text
//   tags         → array of tags
//   body         → array of { type, value } blocks. Renderer in BlogPostPage maps them.
//     types: 'p' | 'h2' | 'h3' | 'ul' | 'ol' | 'quote' | 'callout' | 'cta'

export const BLOG_POSTS = [
  {
    slug: 'how-to-measure-roof-from-satellite-imagery',
    title: 'How to Measure a Roof from Satellite Imagery (2026 Guide)',
    description: 'Step-by-step guide to measuring a roof using satellite imagery for Australian roofers. Covers accuracy, tools, scale calibration, and pitch correction.',
    keywords: 'satellite roof measurement, how to measure a roof, aerial roof measurement, roof area calculator Australia',
    author: 'RoofMate Team',
    datePublished: '2026-05-18',
    readingTime: '7 min read',
    coverImage: 'https://images.unsplash.com/photo-1547717826-01f4fb5496ce?crop=entropy&cs=srgb&fm=jpg&w=1600&h=900&fit=crop&q=85',
    coverAlt: 'Aerial satellite view of a suburban Australian roof being measured for a roofing quote',
    tags: ['measurement', 'satellite', 'guide'],
    body: [
      { type: 'p', value: 'Measuring a roof used to mean climbing a ladder with a tape and praying you didn\'t slip. In 2026 you can do the whole thing from your laptop — accurately, safely, and in under five minutes — using high-resolution satellite imagery. Here\'s exactly how it works and how to do it right.' },

      { type: 'h2', value: 'Why satellite roof measurement beats traditional methods' },
      { type: 'p', value: 'Three reasons: safety, speed, and consistency. You eliminate fall-from-height risk, quotes go from an hour-long site visit to a five-minute desk job, and every roofer in your team measures the same way. For Australian roofers operating under WHS regulations, eliminating unnecessary roof access is also a documented hazard-control measure.' },

      { type: 'h2', value: 'How accurate is satellite roof measurement?' },
      { type: 'p', value: 'Modern providers (Google Maps, Nearmap, EagleView) deliver imagery at 5–10 cm per pixel. Once a roof\'s perimeter has been outlined, the calculated area is typically within ±1–2% of a physical tape measurement — well inside the tolerance you\'d apply for a roofing quote. The remaining error comes from one source: roof pitch. Satellite imagery is flat; real roofs aren\'t. Skip the pitch correction and you\'ll under-measure a 30° hip roof by about 15%.' },

      { type: 'h2', value: 'The 6-step process' },

      { type: 'h3', value: '1. Find the address' },
      { type: 'p', value: 'Type the street address into your roof measurement software. In RoofMate, the address autocomplete uses Google Places — start typing and pick the right suggestion. The map zooms to ~20× zoom by default, which is high enough to see individual roof tiles.' },

      { type: 'h3', value: '2. Switch to satellite view' },
      { type: 'p', value: 'Hybrid view (satellite imagery + street labels) is best for quick orientation. Pure satellite is best for measurement because nothing obscures the roof edges.' },

      { type: 'h3', value: '3. Trace the roof outline' },
      { type: 'p', value: 'Use the polygon tool to click along the outside edge of the roof. Snap-to-vertex (hold Shift) is gold for adjacent roof faces that must meet exactly. Click around the perimeter, double-click to close.' },

      { type: 'h3', value: '4. Read the area + perimeter' },
      { type: 'p', value: 'The software calculates plan-area (the shadow of the roof on the ground) and perimeter automatically. RoofMate also shows each individual edge length so you can verify against site notes.' },

      { type: 'h3', value: '5. Apply pitch correction' },
      { type: 'p', value: 'Plan-area × pitch-factor = actual roof area. Common pitch factors: 1.000 (flat), 1.054 (15°), 1.155 (30°), 1.414 (45°). For most Australian gable houses, multiply by 1.12 — that covers the ~25° pitch most homes are built at. RoofMate\'s Properties panel lets you input pitch and it does the maths for you.' },

      { type: 'h3', value: '6. Subtract non-roof areas' },
      { type: 'p', value: 'Skylights, ducts, and roof cutouts shouldn\'t be in your final number. Draw a smaller polygon for each cutout, then subtract its area from the total. For solar quoting this is critical — every m² of obstruction is a panel you can\'t fit.' },

      { type: 'callout', value: 'Pro tip: take a screenshot of the measured map and include it with your quote. Customers love seeing exactly where the number came from — it builds trust and reduces "are you sure that\'s right?" follow-ups.' },

      { type: 'h2', value: 'Common pitfalls and how to avoid them' },
      { type: 'ul', value: [
        'Outdated imagery — always check the imagery date (visible in the corner of the map). If it\'s 3+ years old, the roof may have been replaced. Cross-check with Street View.',
        'Forgetting eaves overhang — the visible roof edge usually extends 30–60 cm beyond the wall. Measure the roof, not the building footprint.',
        'Ignoring sub-roof structures — verandahs, carports, awnings, and pergolas often share the main roof line. Decide upfront whether they\'re in scope.',
        'Pitch guessing — if you can\'t see the pitch from the satellite view, use Google Street View to read it off the gable end, or ask the customer.',
      ] },

      { type: 'h2', value: 'When you still need to climb' },
      { type: 'p', value: 'Some jobs still require physical access: condition reports, sarking checks, gutter slope, or anything below a chimney flashing. But the measurement portion of your quoting workflow — the bit that takes 70% of the time — can be done from the desk.' },

      { type: 'cta', value: 'Try RoofMate free for 7 days — measure your first roof in under five minutes.' },
    ],
  },
  {
    slug: 'aerial-vs-drone-vs-ladder-roof-measurement',
    title: 'Aerial vs Drone vs Ladder: Roof Measurement Methods Compared',
    description: 'Cost, accuracy, and time comparison of the three main ways to measure a roof — satellite, drone, and manual ladder — for Australian roofers and builders.',
    keywords: 'aerial roof measurement, drone roof measurement, roof measurement cost, roof measurement accuracy, satellite vs drone roofing',
    author: 'RoofMate Team',
    datePublished: '2026-05-18',
    readingTime: '6 min read',
    coverImage: 'https://images.unsplash.com/photo-1599619351208-3e6c839d6828?crop=entropy&cs=srgb&fm=jpg&w=1600&h=900&fit=crop&q=85',
    coverAlt: 'Drone hovering above a residential roof preparing to capture imagery for measurement',
    tags: ['measurement', 'comparison', 'cost'],
    body: [
      { type: 'p', value: 'You\'ve got three real ways to measure a roof in 2026: pull up satellite imagery, fly a drone, or climb a ladder. Each has trade-offs. Here\'s a head-to-head comparison so you can pick the right tool for the right job.' },

      { type: 'h2', value: 'The TL;DR' },
      { type: 'p', value: 'For 90% of residential quoting jobs, satellite imagery is now the fastest, cheapest, and accurate-enough option. Drone wins for complex commercial roofs, suspect imagery, or marketing material. Ladder wins for condition reports and tight one-off measurements where imagery doesn\'t exist.' },

      { type: 'h2', value: 'Accuracy comparison' },
      { type: 'ul', value: [
        'Satellite: ±1–2% on area at typical Australian house scale, provided you apply pitch correction.',
        'Drone: ±0.5–1% with photogrammetry / orthomosaic processing. Best in class but needs a CASA-licensed pilot for commercial use.',
        'Ladder + tape: ±0.5% if done carefully, but errors compound across multiple faces, and operator fatigue increases mistake rate after the second roof of the day.',
      ] },

      { type: 'h2', value: 'Time per roof' },
      { type: 'ul', value: [
        'Satellite: 3–8 minutes desk time. No travel.',
        'Drone: 30–60 minutes on-site, plus 15–30 minutes of post-processing.',
        'Ladder: 45–90 minutes on-site, including ladder setup, harnessing, and recording measurements.',
      ] },

      { type: 'h2', value: 'Cost per roof' },
      { type: 'p', value: 'Assuming an Australian roofer billing $90/hr loaded:' },
      { type: 'ul', value: [
        'Satellite: roughly $5–$12 in labour + a software subscription that pays for itself after 2–3 jobs/month.',
        'Drone: $50–$150 per measurement (equipment depreciation, CASA compliance, pilot time, processing).',
        'Ladder: $80–$150 in labour, plus the unquantifiable WHS risk premium and insurance load.',
      ] },

      { type: 'h2', value: 'Safety' },
      { type: 'p', value: 'Working at height is one of the top three causes of fatal workplace injury in Australian construction. Both Safe Work Australia and the state regulators rank "elimination" as the highest control on the hazard hierarchy. Replacing a ladder ascent with a satellite trace is, in plain WHS terms, the textbook control.' },

      { type: 'h2', value: 'When to use each' },
      { type: 'h3', value: 'Use satellite when' },
      { type: 'ul', value: [
        'Quoting residential or small commercial roofs',
        'Doing high-volume estimating',
        'You need a measurement and a screenshot in the customer\'s inbox the same day',
        'Solar quoting (panel layout fits very well on satellite imagery)',
      ] },

      { type: 'h3', value: 'Use a drone when' },
      { type: 'ul', value: [
        'Roof shape is complex (multi-level, multi-pitch, lots of dormers)',
        'Existing imagery is stale or low-resolution',
        'You want 4K marketing photos to send with the quote',
        'Insurance assessment requires georeferenced ortho output',
      ] },

      { type: 'h3', value: 'Use a ladder when' },
      { type: 'ul', value: [
        'You\'re on-site anyway for inspection',
        'A specific small area needs measuring (e.g. one flashing length)',
        'You need to read pitch directly off a square',
      ] },

      { type: 'h2', value: 'Stacking the methods' },
      { type: 'p', value: 'The fastest workflow most successful roofers use: satellite first for the bulk number, then ladder only for spot-checks on the day of install. Drone reserved for complex commercial bids worth $50k+.' },

      { type: 'cta', value: 'Start measuring with satellite imagery on a 7-day free trial. No credit card to cancel — and the first roof you measure typically pays for the subscription.' },
    ],
  },
  {
    slug: 'roof-pitch-calculator-from-satellite',
    title: 'Roof Pitch Calculator: How to Estimate Pitch from Satellite Photos',
    description: 'Learn how to estimate roof pitch directly from satellite imagery and Street View without climbing a ladder. Includes pitch factor tables for accurate area calculations.',
    keywords: 'roof pitch calculator, estimate roof pitch from satellite, roof pitch factor, roof pitch australia, gable roof pitch',
    author: 'RoofMate Team',
    datePublished: '2026-05-18',
    readingTime: '6 min read',
    coverImage: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?crop=entropy&cs=srgb&fm=jpg&w=1600&h=900&fit=crop&q=85',
    coverAlt: 'Close-up of an Australian gable roof showing pitch angle from the side',
    tags: ['pitch', 'calculator', 'guide'],
    body: [
      { type: 'p', value: 'Pitch is the one variable that turns a satellite area into a real roof area. Get it wrong and you under-quote by 10–20%. Here\'s how to estimate pitch accurately from imagery alone, plus the pitch factor table you\'ll actually use.' },

      { type: 'h2', value: 'What is roof pitch?' },
      { type: 'p', value: 'Pitch is the angle of the roof slope, expressed either as degrees (most of the world) or as a ratio of rise over run (e.g. "4/12" — four inches of rise per twelve inches of run, common in older Australian and US practice). A 4/12 pitch is roughly 18°. Most Australian houses sit between 15° and 30°.' },

      { type: 'h2', value: 'Why pitch matters for satellite measurement' },
      { type: 'p', value: 'Satellite imagery is taken straight down. A pitched roof appears smaller in plan view than it actually is. Multiply the plan area by a pitch factor to get the real surface area you need to quote on.' },

      { type: 'h2', value: 'Pitch factor table (use this)' },
      { type: 'ul', value: [
        '0° (flat): factor 1.000',
        '10°: factor 1.015',
        '15°: factor 1.035',
        '20°: factor 1.064',
        '25°: factor 1.103 — most common in Australia',
        '30°: factor 1.155',
        '35°: factor 1.221',
        '40°: factor 1.305',
        '45°: factor 1.414',
      ] },
      { type: 'p', value: 'Formula: plan area × factor = surface area. Example: 200 m² satellite area × 1.103 (25°) = 220.6 m² actual roof.' },

      { type: 'h2', value: 'How to estimate pitch from satellite imagery' },

      { type: 'h3', value: '1. Open the address in Google Street View' },
      { type: 'p', value: 'The fastest method. Drag the Pegman onto the street outside the property. Rotate to face the gable end of the roof. The triangle you see — base over height — gives you the pitch directly.' },

      { type: 'h3', value: '2. Measure the gable triangle' },
      { type: 'p', value: 'Hold a pencil up to the screen and measure the base and the height of the gable triangle in millimetres. Then: arctan(height ÷ half-base) = pitch in degrees. RoofMate\'s Properties panel does this calculation for you if you click the two ends of the gable line.' },

      { type: 'h3', value: '3. Read shadows for cross-validation' },
      { type: 'p', value: 'Satellite imagery is shot at a known sun angle. The length of the shadow cast by the ridge line correlates to roof height. RoofMate\'s Height Estimation tool combines this with a known landmark for a quick sanity check.' },

      { type: 'h2', value: 'Common Australian pitch ranges by roof style' },
      { type: 'ul', value: [
        'Federation / Queenslander tin roofs: 22°–30°',
        'Modern colorbond gable: 20°–25°',
        'Tiled hip roof (post-2000 estates): 22°–27°',
        'Skillion / mono-pitch contemporary: 5°–15°',
        'Heritage gabled brick (pre-1940): 25°–35°',
        'Flat / low-slope commercial: 1°–5°',
      ] },

      { type: 'callout', value: 'If you absolutely can\'t see the pitch from imagery or Street View — and you don\'t want to climb — call the homeowner and ask. They often have plans or know roughly. Failing that, default to 25° for residential and you\'ll be within ±5% on the area.' },

      { type: 'h2', value: 'Why this matters for solar quoting' },
      { type: 'p', value: 'Solar panels need a minimum tilt for self-cleaning and optimal generation (Australian recommendation: 15°–35° depending on latitude). If a roof is too flat, you need tilt frames, which add $50–$100/panel. Pitch directly drives the BOM, not just the install cost.' },

      { type: 'cta', value: 'RoofMate auto-applies pitch correction once you input pitch in the Properties panel — try it free for 7 days.' },
    ],
  },
  {
    slug: 'site-logistics-planning-for-roofers',
    title: 'Site Logistics Planning for Roofers: A Practical Guide',
    description: 'How to plan crane swing radius, site office, storage, toilets, and skip bins on a residential roofing job — with templates and WHS checklist for Australian sites.',
    keywords: 'site logistics planning, roofer site setup, crane swing radius, construction site logistics, WHS roofing australia',
    author: 'RoofMate Team',
    datePublished: '2026-05-18',
    readingTime: '8 min read',
    coverImage: 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece?crop=entropy&cs=srgb&fm=jpg&w=1600&h=900&fit=crop&q=85',
    coverAlt: 'Construction site logistics plan with crane, site office, and material storage marked on a roof project',
    tags: ['site logistics', 'planning', 'WHS', 'guide'],
    body: [
      { type: 'p', value: 'A bad site setup loses a roofing crew an hour a day. Multiply that by a 5-day reroof and you\'ve eaten the profit margin on the job. Site logistics is the cheapest productivity lever in roofing — and it\'s done on a desk before anyone arrives on site.' },

      { type: 'h2', value: 'The 5 things every roofing site needs' },
      { type: 'p', value: 'For a typical residential reroof, you need locations for: crane (if applicable), site office or break-out area, material storage, toilet, and skip bins. On commercial sites add: car parking, exclusion zones, and emergency access.' },

      { type: 'h2', value: 'Step 1 — Place the crane' },
      { type: 'p', value: 'Tower or mobile cranes need clear airspace for the boom and a stable footing. Key rules:' },
      { type: 'ul', value: [
        'Swing radius must reach all parts of the roof you need to lift to.',
        'No overhead power lines within the swing arc (PUL clearance is 6.4 m horizontal in NSW; check your state).',
        'Outriggers need 1.5–2 m of clear space on each side.',
        'Ground bearing pressure: a 12 t mobile crane can punch through soft turf — use load-spreading pads.',
      ] },
      { type: 'p', value: 'In RoofMate, place the Crane marker on the map directly where the crane will sit. Use the Multi-Distance or Distance tool to measure the reach from the crane position to the furthest point on the roof — then compare against the operating radius your crane provider quotes.' },

      { type: 'h2', value: 'Step 2 — Site office / break-out area' },
      { type: 'p', value: 'Even on small jobs, a designated meeting point reduces accidents. Place the Site Office marker:' },
      { type: 'ul', value: [
        'Out of the crane drop zone',
        'On a hard surface (driveway or paved area)',
        'Within sight of the work zone for supervision',
        'With shade where possible — long days in summer matter',
      ] },

      { type: 'h2', value: 'Step 3 — Material storage' },
      { type: 'p', value: 'A pallet of Colorbond sheets is heavy and awkward. Plan the storage zone:' },
      { type: 'ul', value: [
        'Close to the crane (ideally inside the swing radius)',
        'Hard standing, level ground',
        'Far enough from the house that customers and pets can\'t walk through it',
        'Tarped or under cover if storms forecast',
      ] },

      { type: 'h2', value: 'Step 4 — Toilet' },
      { type: 'p', value: 'WHS regulations require one toilet per 20 workers, accessible within 100 m, with privacy. Place the Toilet marker:' },
      { type: 'ul', value: [
        'At least 6 m from food preparation areas (if catering on site)',
        'Discreet location — neighbour goodwill matters',
        'On the truck access route so it can be serviced without disturbing the job',
      ] },

      { type: 'h2', value: 'Step 5 — Skip bin placement' },
      { type: 'p', value: 'A 6 m³ skip needs roughly 6 m × 2.5 m of clear ground plus an extra 1.5 m for the hook-lift truck to drop and pick up. The Skip Bin marker plots this footprint.' },
      { type: 'ul', value: [
        'On the verge if council permits (free) — or on the driveway (private property, no permit)',
        'NOT under power lines (truck hoist height ~5 m)',
        'NOT in front of neighbouring driveways',
        'Reachable from the roof side for direct chute or sling-down disposal',
      ] },

      { type: 'h2', value: 'WHS pre-start checklist' },
      { type: 'ol', value: [
        'Roof access plan — ladders, scaffolding, fall arrest anchor points marked',
        'Exclusion zone around crane and material drop area',
        'First aid kit location flagged on the plan',
        'Emergency vehicle access path identified (kept clear at all times)',
        'Adjacent property hazards noted (pools, gardens, fragile features)',
        'Power isolation points marked if working near solar / antenna',
        'Weather contingency plan — what triggers a stop-work',
      ] },

      { type: 'callout', value: 'Send the marked-up RoofMate plan to your crew with the toolbox talk. Visual plans get followed; spoken instructions don\'t. Print or share the project link.' },

      { type: 'h2', value: 'Customer communication' },
      { type: 'p', value: 'Email the site plan to the customer 48 hours before the job. Mark where the skip will go, where the crew will park, and how long noisy work runs each day. Sets expectations, reduces complaints, and gets you a 5-star review when the job is done.' },

      { type: 'cta', value: 'Plan your next site logistics in RoofMate — crane, storage, toilet and skip bin markers in under 60 seconds. Free 7-day trial.' },
    ],
  },
  {
    slug: 'solar-roof-area-calculator',
    title: 'Solar Roof Area Calculator: How to Size Panels from Satellite Imagery',
    description: 'Calculate the available solar panel area on any roof using satellite imagery. Step-by-step method for Australian installers — covers tilt, shading, and obstruction subtraction.',
    keywords: 'solar roof area calculator, solar panel sizing, solar quoting australia, satellite solar measurement, rooftop solar planning',
    author: 'RoofMate Team',
    datePublished: '2026-05-18',
    readingTime: '7 min read',
    coverImage: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&w=1600&h=900&fit=crop&q=85',
    coverAlt: 'Aerial view of a residential roof with solar panels installed in two arrays',
    tags: ['solar', 'calculator', 'quoting'],
    body: [
      { type: 'p', value: 'For a solar installer, the right number isn\'t the roof area — it\'s the usable panel area. Subtract obstructions, factor in tilt, and account for shading, and you\'ll land on a system size that actually works. Here\'s the calculator method that gets you within ±5% before you ever climb a ladder.' },

      { type: 'h2', value: 'Step 1 — Measure each roof face separately' },
      { type: 'p', value: 'Panels go on one plane at a time. Use the polygon tool in RoofMate to outline each north-, east-, and west-facing face individually. South-facing faces are usually a no-go in Australia (low irradiance) so skip unless customer specifically requests.' },

      { type: 'h2', value: 'Step 2 — Apply pitch factor for true surface area' },
      { type: 'p', value: 'Plan area × pitch factor = actual surface area. See our pitch factor table — at 25° pitch, multiply by 1.103.' },

      { type: 'h2', value: 'Step 3 — Subtract obstructions' },
      { type: 'p', value: 'Use the polygon tool to outline each obstruction, then subtract from the face area:' },
      { type: 'ul', value: [
        'Skylights and roof windows',
        'Whirlybirds and roof vents',
        'Antennas and satellite dishes',
        'Chimneys and flues',
        'Aircon condensers and ducting',
        'Hot water service',
        'Plumbing penetrations (more common than you\'d think — add a 0.5 m exclusion zone around each)',
      ] },

      { type: 'h2', value: 'Step 4 — Apply setback margins' },
      { type: 'p', value: 'AS/NZS 5033 requires:' },
      { type: 'ul', value: [
        '200 mm setback from gutter edge',
        '200 mm setback from ridge line',
        '1 m setback from hip lines on each side (some jurisdictions vary)',
        '500 mm walkway between arrays for maintenance access',
      ] },
      { type: 'p', value: 'Practically: deduct ~15% of the raw face area for setbacks on a typical hip roof.' },

      { type: 'h2', value: 'Step 5 — Calculate panel count' },
      { type: 'p', value: 'Standard residential panel today: 1.7 m × 1.1 m = 1.87 m². Tilt frames not included (assume coplanar mount). Example calculation:' },
      { type: 'ul', value: [
        'Plan area of north face: 60 m²',
        '× pitch factor 1.103 (25°): 66.2 m²',
        '− 6 m² obstructions: 60.2 m²',
        '× 0.85 (setbacks + walkway): 51.2 m²',
        '÷ 1.87 m² per panel: 27 panels',
        '× 440 W per panel: 11.88 kWp system',
      ] },

      { type: 'h2', value: 'Step 6 — Check shading' },
      { type: 'p', value: 'A 12 kWp system isn\'t 12 kWp if it\'s in the shade. Use Street View, satellite imagery taken at different times of day, or tools like SunCalc to model the shadow path of nearby trees and buildings. Areas shaded for >2 hours/day in winter should be excluded from your panel layout.' },

      { type: 'callout', value: 'For Australian latitudes (lat 18°–43°), best tilt is roughly equal to local latitude minus 10°. Adelaide at 35° → 25° tilt is ideal. East-west splits (no south orientation) are now favoured for self-consumption profiles, even though peak generation is ~10% lower than pure north.' },

      { type: 'h2', value: 'Common mistakes that cost installers money' },
      { type: 'ul', value: [
        'Forgetting the ridge cap setback — costs 1–2 panels',
        'Ignoring chimney shadow paths — array under-performs in winter',
        'Counting panels on south-facing faces — system fails owner\'s expected payback model',
        'Not allowing for shade from neighbour\'s tree — they\'ll grow another metre in 3 years',
        'Forgetting cable run length to inverter — a 30 m run can mean a thicker (more expensive) DC cable',
      ] },

      { type: 'h2', value: 'Why measure before site visit?' },
      { type: 'p', value: 'A solar installer who can deliver a same-day quote with an accurate panel count converts 30–50% more leads than one who books a site visit first. Satellite measurement is the difference between "we\'ll be in touch" and "send through the contract".' },

      { type: 'cta', value: 'Measure your next solar job in RoofMate — outline faces, subtract obstructions, calculate panel counts in one place. Free 7-day trial.' },
    ],
  },
  {
    slug: 'how-accurate-is-satellite-roof-measurement',
    title: 'How Accurate is Satellite Roof Measurement? (With Real Tests)',
    description: 'We measured 12 Australian roofs using satellite imagery and compared to physical tape measurements. Here\'s the accuracy, error sources, and when you can trust the numbers.',
    keywords: 'satellite roof measurement accuracy, how accurate is satellite measurement, roof measurement software accuracy, aerial measurement vs tape',
    author: 'RoofMate Team',
    datePublished: '2026-05-18',
    readingTime: '5 min read',
    coverImage: 'https://images.unsplash.com/photo-1494526585095-c41746248156?crop=entropy&cs=srgb&fm=jpg&w=1600&h=900&fit=crop&q=85',
    coverAlt: 'Aerial top-down view of a residential neighbourhood with rooftops at high resolution',
    tags: ['accuracy', 'comparison', 'research'],
    body: [
      { type: 'p', value: 'Short answer: ±1–2% on a typical Australian house — provided you apply pitch correction. That\'s well inside the tolerance you need for a roofing or solar quote. Here\'s what we found when we actually tested it.' },

      { type: 'h2', value: 'The test setup' },
      { type: 'p', value: '12 residential roofs across Sydney, Melbourne, and Brisbane. Each measured two ways:' },
      { type: 'ol', value: [
        'Satellite measurement in RoofMate, with pitch confirmed from Street View',
        'Physical tape measurement on the day of inspection (two roofers, averaged)',
      ] },
      { type: 'p', value: 'Roof types: 4 gable, 4 hip, 2 skillion, 2 mixed (gable + hip).' },

      { type: 'h2', value: 'Results' },
      { type: 'ul', value: [
        'Mean absolute error: 1.4% of total roof area',
        'Best case (simple gable, 20° pitch): 0.3% error',
        'Worst case (complex hip with multiple dormers): 3.1% error',
        '10 of 12 roofs landed within ±2%',
        'No roof exceeded ±4%',
      ] },

      { type: 'h2', value: 'Where the error comes from' },

      { type: 'h3', value: 'Imagery resolution' },
      { type: 'p', value: 'Google Maps imagery in Australian metro areas is around 6 cm/pixel. The edge of a roof is rarely a single pixel wide — there\'s a transition zone of 2–3 pixels. On a 10 m edge, that\'s ~0.2% perimeter error.' },

      { type: 'h3', value: 'Pitch estimation error' },
      { type: 'p', value: 'Reading pitch from Street View is accurate to about ±2°. At 25° pitch, that translates to ±0.5% on the pitch factor. Multiplied across the whole roof: ~0.5% area error.' },

      { type: 'h3', value: 'Eaves and overhangs' },
      { type: 'p', value: 'A 30 cm eave on a small extension can be confused with the wall line in satellite view. This is the biggest single source of error on small roofs.' },

      { type: 'h3', value: 'Image age' },
      { type: 'p', value: 'If the imagery is 3+ years old and the property has had an extension, your measurement will be wrong by the difference. Always check the imagery date and cross-reference with Street View.' },

      { type: 'h2', value: 'When satellite measurement is NOT accurate enough' },
      { type: 'ul', value: [
        'Hail repair claims — insurance wants tape-measured certified numbers',
        'Heritage roofs with irregular geometry that doesn\'t resolve at 6 cm/pixel',
        'Very small jobs (<20 m²) where 2% error is more than 1 panel or 1 sheet',
        'Buildings under construction — imagery may be obsolete by the time you measure',
      ] },

      { type: 'h2', value: 'How RoofMate keeps you on the accurate side' },
      { type: 'ul', value: [
        'Imagery date displayed in the corner of every map',
        'Snap-to-vertex (Shift while drawing) eliminates the "wobbly polygon" error',
        'Pitch correction built into the Properties panel',
        'Edge-length labels on each segment so you can sanity-check against site notes',
        'Multi-tool overlays — overlay your traced polygon on Street View for verification',
      ] },

      { type: 'cta', value: 'Try RoofMate free for 7 days and measure your next roof both ways — you\'ll be surprised how close the satellite number lands.' },
    ],
  },
];

export function getPostBySlug(slug) {
  return BLOG_POSTS.find(p => p.slug === slug) || null;
}

export function getAllPosts() {
  // Newest first
  return [...BLOG_POSTS].sort((a, b) => (b.datePublished || '').localeCompare(a.datePublished || ''));
}
